import React, { useEffect, useMemo, useState } from 'react';
import { iceFetchJson } from './iceApi';
import { GoalCard, KpiRow } from './iceUi';

function Measurements({ kpiId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function run() {
      setLoading(true);
      setError('');
      try {
        const data = await iceFetchJson(`/tenants/current/ice/published/measurements?kpi_id=${encodeURIComponent(kpiId)}`);
        if (!alive) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }
    if (kpiId) run();
    return () => {
      alive = false;
    };
  }, [kpiId]);

  if (!kpiId) return null;
  if (loading) return <div className="text-sm text-slate-600">Laddar mätvärden…</div>;
  if (error) return <div className="text-sm text-red-700">{error}</div>;

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <div className="text-sm text-slate-600">Inga mätvärden.</div>
      ) : (
        items.map((m) => (
          <div key={m.id} className="rounded border border-slate-200 bg-white p-3">
            <div className="text-sm font-semibold">{m.value}</div>
            <div className="text-xs text-slate-600">
              {m.measured_at ? `Datum: ${m.measured_at}` : 'Datum: -'}
              {m.note ? ` • ${m.note}` : ''}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Actions({ kpiId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function run() {
      setLoading(true);
      setError('');
      try {
        const data = await iceFetchJson(`/tenants/current/ice/published/actions?kpi_id=${encodeURIComponent(kpiId)}`);
        if (!alive) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }
    if (kpiId) run();
    return () => {
      alive = false;
    };
  }, [kpiId]);

  if (!kpiId) return null;
  if (loading) return <div className="text-sm text-slate-600">Laddar åtgärder…</div>;
  if (error) return <div className="text-sm text-red-700">{error}</div>;

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <div className="text-sm text-slate-600">Inga publicerade åtgärder.</div>
      ) : (
        items.map((a) => (
          <div key={a.id} className="rounded border border-slate-200 bg-white p-3">
            <div className="text-sm font-semibold">{a.title}</div>
            <div className="text-xs text-slate-600">
              {a.state ? `Status: ${a.state}` : ''}
              {a.due_at ? ` • Förfaller: ${a.due_at}` : ''}
              {a.owner_role ? ` • Ansvar: ${a.owner_role}` : ''}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default function IcePublishedPanel() {
  const [goals, setGoals] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const g = await iceFetchJson('/tenants/current/ice/published/goals');
        if (!alive) return;
        const items = Array.isArray(g?.items) ? g.items : [];
        setGoals(items);
        setSelectedGoal(items[0] || null);
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    async function loadKpis() {
      setError('');
      setKpis([]);
      setSelectedKpi(null);
      if (!selectedGoal?.id) return;
      try {
        const data = await iceFetchJson(`/tenants/current/ice/published/kpis?goal_id=${encodeURIComponent(selectedGoal.id)}`);
        if (!alive) return;
        setKpis(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      }
    }
    loadKpis();
    return () => {
      alive = false;
    };
  }, [selectedGoal?.id]);

  const title = useMemo(() => {
    if (!selectedGoal) return 'ICE';
    return `ICE – ${selectedGoal.code || 'Mål'}: ${selectedGoal.title}`;
  }, [selectedGoal]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">{title}</h1>
        <div className="text-sm text-slate-600">Visar endast publicerat innehåll.</div>
      </div>

      {loading ? <div className="text-sm text-slate-600">Laddar…</div> : null}
      {error ? <div className="text-sm text-red-700">{error}</div> : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <div className="text-sm font-semibold">Mål</div>
          {goals.length === 0 ? (
            <div className="text-sm text-slate-600">Inga publicerade mål.</div>
          ) : (
            goals.map((g) => (
              <GoalCard
                key={g.id}
                goal={g}
                selected={selectedGoal?.id === g.id}
                onSelect={(goal) => {
                  setSelectedGoal(goal);
                  setSelectedKpi(null);
                }}
              />
            ))
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold">KPI:er</div>
          {selectedGoal ? (
            kpis.length === 0 ? (
              <div className="text-sm text-slate-600">Inga publicerade KPI:er för valt mål.</div>
            ) : (
              <div className="space-y-2">
                {kpis.map((k) => (
                  <KpiRow key={k.id} kpi={k} selected={selectedKpi?.id === k.id} onSelect={setSelectedKpi} />
                ))}
              </div>
            )
          ) : (
            <div className="text-sm text-slate-600">Välj ett mål.</div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold">Mätvärden</div>
          {selectedKpi ? (
            <>
              <Measurements kpiId={selectedKpi.id} />
              <div className="pt-3">
                <div className="text-sm font-semibold">Åtgärder</div>
                <div className="mt-2">
                  <Actions kpiId={selectedKpi.id} />
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-600">Välj en KPI för att se mätvärden.</div>
          )}
        </div>
      </div>
    </div>
  );
}
